import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** How the lobby "Present" list renders each character. */
export type PresentViewMode = 'icons' | 'list';

/** The data for one Present entry, independent of how it's rendered. */
export interface PresentEntryData {
  name: string;
  avatarSrc?: string;
  avatarFallback: ReactNode;
  tooltip: ReactNode;
  /** Secondary line shown under the name in list view only (e.g. "Level 1 Elf Wizard"). */
  description?: string;
  ariaLabel: string;
  onClick: () => void;
  /** Staff NPCs render their name in bold. */
  bold?: boolean;
  /** The current user's own character gets a green ring instead of the focus ring. */
  selfRing?: boolean;
}

interface PresentEntryProps extends PresentEntryData {
  viewMode: PresentViewMode;
}

/**
 * A single character in the lobby "Present" list. Renders as a centered
 * avatar-over-name tile (`icons`) or a horizontal avatar + name row (`list`).
 */
export function PresentEntry({
  name,
  avatarSrc,
  avatarFallback,
  tooltip,
  description,
  ariaLabel,
  onClick,
  viewMode,
  bold = false,
  selfRing = false,
}: PresentEntryProps) {
  const isList = viewMode === 'list';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            aria-label={ariaLabel}
            className={cn(
              // Focus ring lives on the button (the focusable element), not the Avatar.
              // ring-ring is theme-aware (visible in light + dark); ring-inset avoids the
              // parent overflow-hidden clipping the ring's edges. cursor-pointer opts back
              // in: Tailwind v4 Preflight defaults buttons to cursor: default.
              'cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              isList
                ? 'flex items-center gap-2 w-full px-1.5 py-1 hover:bg-muted transition-colors'
                : 'flex flex-col items-center gap-1 w-16 hover:scale-110 transition-transform'
            )}
          >
            <Avatar
              className={cn(
                'shrink-0',
                isList ? 'h-8 w-8' : 'h-14 w-14',
                selfRing && 'ring-2 ring-green-500 ring-offset-background'
              )}
            >
              <AvatarImage src={avatarSrc} alt={name} />
              <AvatarFallback
                className={cn(
                  'bg-muted',
                  isList ? 'text-xs' : 'text-base',
                  selfRing && 'bg-primary/20'
                )}
              >
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            {isList ? (
              <span className="flex min-w-0 flex-col text-left">
                <span
                  className={cn(
                    'text-xs font-character text-foreground truncate',
                    bold && 'font-bold'
                  )}
                >
                  {name}
                </span>
                {description && (
                  <span className="text-[10px] text-foreground/70 truncate">{description}</span>
                )}
              </span>
            ) : (
              <span
                className={cn(
                  'text-xs font-character text-foreground truncate w-full text-center',
                  bold && 'font-bold'
                )}
              >
                {name}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={0}>
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
